import { useQuery } from "@tanstack/react-query";
import AnalysisContainer from "./AnalysisContainer";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import Container from "../../components/Container";
import Text from "../../components/Text";
import useAuthHttp from "../../hooks/useAuthHttp";
import { IAssetTypesResponse } from "../../models/asset";
import SubGroupMultiSelect from "../../components/SubGroupMultiSelect";
import GroupMultiSelect from "../../components/GroupMultiSelect";
import { Card } from "primereact/card";
import Currency from "../../components/Currency";
import { Chart } from "primereact/chart";
import { months } from "../../utils/date";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import AssetsMultiSelect from "../../components/AssetsMultiSelect";
import { Button } from "primereact/button";
import Icon from "../../components/Icon";
import { Tooltip } from "primereact/tooltip";

interface IEvolutionPageFilters {
    group: string[];
    subGroup: string[];
    assets: string[];
}

interface IMonthEvolution {
    month_reference: string;
    month_evolution_value: number;
    month_initial_balance: number;
    month_total_charges: number;
    month_new_asset_value: number;
    month_total_withdrawals: number;
    month_total_earnings: number;
    month_total_income: number;
    month_total_equity: number;
    month_capital_gain: number;
}

interface IEquityEvolution {
    user_id: string;
    initial_balance: number;
    total_charges: number;
    total_withdrawals: number;
    total_earnings: number;
    total_income: number;
    total_equity: number;
    capital_gain: number;
    equity_evolution_list: IMonthEvolution[];
}

interface IEquityAnalysisResponse {
    equity_analysis: IEquityEvolution;
}

const EvolutionPage = () => {
    const authHttp = useAuthHttp();
    const [period, setPeriod] = useState<Date[]>([]);
    const [filters, setFilters] = useState<IEvolutionPageFilters>({
        group: [],
        subGroup: [],
        assets: []
    });
    const [groupLabel, setGroupLabel] = useState<string>("");
    const [subGroupLabel, setSubGroupLabel] = useState<string>("");
    const [assetsLabel, setAssetsLabel] = useState<string>("");
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

    useEffect(() => {
        setSelectedYear(period[1]?.getFullYear() ?? new Date().getFullYear());
    }, [period]);

    const documentStyle = getComputedStyle(document.documentElement);
    const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

    const evolutionQuery = useQuery({
        queryKey: [
            "evolution",
            period.length == 2 ? moment(period[0]).format("YYYY-MM-DD") : "",
            period.length == 2 ? moment(period[1]).format("YYYY-MM-DD") : "",
            filters.group.join(","),
            filters.subGroup.join(","),
            filters.assets.join(",")
        ],
        queryFn: async () => {
            const searchParams = new URLSearchParams();
            if (period.length == 2) {
                searchParams.append("start_date", moment(period[0]).format("YYYY-MM-DD"));
                searchParams.append("end_date", moment(period[1]).format("YYYY-MM-DD"));
            }

            searchParams.append("asset_group_desc", filters.group.join(","));
            searchParams.append("asset_group_id", filters.subGroup.join(","));
            searchParams.append("asset_user_id_list", filters.assets.join(","));
            const response = await authHttp.doGet<IEquityAnalysisResponse>(`analysis/evolution?${searchParams.toString()}`);

            const equityEvolutionList = Object.values(response.equity_analysis.equity_evolution_list)
                .map((item) => 
                    Object.values(item)
                        .reduce((acc, value) => {
                            return {
                                ...acc,
                                ...value
                            };
                        }
                ));

            return {
                ...response,
                equity_analysis: {
                    ...response.equity_analysis,
                    equity_evolution_list: equityEvolutionList.sort((a, b) => {
                        return moment(a.month_reference, "YYYYMM").toDate().getTime() - moment(b.month_reference, "YYYYMM").toDate().getTime();
                    })
                }
            };
        }
    });

    const assetListTypeQuery = useQuery({
        queryKey: ["assetListType"],
        queryFn: async () =>
            await authHttp.doGet<IAssetTypesResponse>('asset/list_types')
    });

    const evolutionYear = useMemo(() => Array.from({length: 12}).map((_, i) => {
        const month = i + 1;
        const year = selectedYear;

        const monthData = evolutionQuery.data?.equity_analysis.equity_evolution_list.find(event => moment(event.month_reference, "YYYYMM").isSame(moment(`${year}${month.toString().padStart(2, "0")}`, "YYYYMM"), "month"));

        if (monthData) {
            return monthData;
        }

        return {
            month_reference: `${year}${month.toString().padStart(2, "0")}`,
            month_evolution_value: 0,
            month_initial_balance: 0,
            month_total_charges: 0,
            month_total_withdrawals: 0,
            month_total_earnings: 0,
            month_total_income: 0,
            month_total_equity: 0,
            month_capital_gain: 0
        } as IMonthEvolution;
    }), [evolutionQuery.data?.equity_analysis.equity_evolution_list, selectedYear]);

    return (
        <AnalysisContainer
            loading={evolutionQuery.isLoading}
            period={period}
            setPeriod={setPeriod}
            showExport={{ filename: "evolução" }}
            filters={[
                {
                    name: "Grupos",
                    content: <GroupMultiSelect 
                        value={filters.group} 
                        onChange={(value) => {
                            setFilters(old => ({ ...old, group: value }));
                        }} 
                        onChangeSelectedLabel={setGroupLabel}
                        assets={assetListTypeQuery.data?.asset_list ?? []}
                    />,
                    chip: { label: groupLabel, icon: "pi-tags" }
                },
                {
                    name: "Subgrupos",
                    content: <SubGroupMultiSelect 
                        selectedGroupFamily={filters.group}
                        value={filters.subGroup} 
                        onChange={(value) => {
                            setFilters(old => ({ ...old, subGroup: value }));
                        }} 
                        onChangeSelectedLabel={setSubGroupLabel}
                        assets={assetListTypeQuery.data?.asset_list ?? []}
                    />,
                    chip: { label: subGroupLabel, icon: "pi-sitemap" }
                },
                {
                    name: "Ativos",
                    content: <AssetsMultiSelect
                        period={period}
                        value={filters.assets}
                        onChange={(value) => {
                            setFilters(old => ({ ...old, assets: value }));
                        }}
                        onChangeSelectedLabel={setAssetsLabel}
                    />,
                    chip: { label: assetsLabel, icon: "pi-chart-bar" }
                }
            ]}
        >
            <Container flexColumn fullWidth gap={3}>
                <Container wrap gap={3} fullWidth>
                    <Container className="flex flex-1 bg-white border-1 border-gray-300 gap-2 p-3 border-round justify-center items-center" flexColumn>
                        <Text className="text-sm text-900">Saldo inicial</Text>
                        <Currency value={evolutionQuery.data?.equity_analysis.initial_balance ?? 0}>
                            {(value) => (
                                <Text className="font-bold text-900 text-lg">{value}</Text>
                            )}
                        </Currency>
                    </Container>

                    <Container className="flex flex-1 bg-white border-1 border-gray-300 gap-2 p-3 border-round justify-center items-center" flexColumn>
                        <Text className="text-sm text-900">Aportes</Text>
                        <Currency value={evolutionQuery.data?.equity_analysis.total_charges ?? 0}>
                            {(value) => (
                                <Text className="font-bold text-lg" color="primary">{value}</Text>
                            )}
                        </Currency>
                    </Container>

                    <Container className="flex flex-1 bg-white border-1 border-gray-300 gap-2 p-3 border-round justify-center items-center" flexColumn>
                        <Text className="text-sm text-900">Resgates</Text>
                        <Currency value={evolutionQuery.data?.equity_analysis.total_withdrawals ?? 0}>
                            {(value) => (
                                <Text className="font-bold text-lg" color="attention">{value}</Text>
                            )}
                        </Currency>
                    </Container>
                </Container>

                <Container wrap gap={3} fullWidth>
                    <Container className="flex flex-1 bg-white border-1 border-gray-300 gap-2 p-3 border-round justify-center items-center" flexColumn>
                        <Text className="text-sm text-900">Proventos</Text>
                        <Currency value={evolutionQuery.data?.equity_analysis.total_earnings ?? 0}>
                            {(value) => (
                                <Text className="font-bold text-lg" color="green-500">{value}</Text>
                            )}
                        </Currency>
                    </Container>

                    <Container className="flex flex-1 bg-white border-1 border-gray-300 gap-2 p-3 border-round justify-center items-center" flexColumn>
                        <Text className="text-sm text-900">Rendimentos</Text>
                        <Currency value={evolutionQuery.data?.equity_analysis.total_income ?? 0}>
                            {(value) => (
                                <Text className="font-bold text-lg" color={(evolutionQuery.data?.equity_analysis.total_income ?? 0) > 0 ? "green-500" : "attention"}>{value}</Text>
                            )}
                        </Currency>
                    </Container>

                    <Container className="flex flex-1 bg-white border-1 border-gray-300 gap-2 p-3 border-round justify-center items-center" flexColumn>
                        <Text className="text-sm text-900">Ganho de capital</Text>
                        <Currency value={evolutionQuery.data?.equity_analysis.capital_gain ?? 0}>
                            {(value) => (
                                <Text className="font-bold text-lg" color="green-500">{value}</Text>
                            )}
                        </Currency>
                    </Container>
                    
                    <Container className="flex flex-1 bg-white border-1 border-gray-300 gap-2 p-3 border-round justify-center items-center" flexColumn>
                        <Text className="text-sm text-900 gap-2 flex">
                            Saldo Final
                            <Tooltip position="left" content="O saldo final é apresentado sem considerar o valor do resgate e o ganho de capital." target=".saldo-final-info" />
                            <Icon name="pi pi-info-circle" className="saldo-final-info" />
                        </Text>
                        <Currency value={evolutionQuery.data?.equity_analysis.total_equity ?? 0}>
                            {(value) => (
                                <Text className="font-bold text-900 text-lg">{value}</Text>
                            )}
                        </Currency>
                    </Container>
                </Container>

                <Card
                    title="Evolução Patrimonial"
                    subTitle="Variação do patrimônio ao longo do período"
                    className="w-full"
                >
                    <Container className="justify-content-end align-items-center" gap={2}>
                        <Button
                            icon="pi pi-arrow-left"
                            outlined
                            rounded
                            className="border-none"
                            onClick={() => {
                                setSelectedYear(selectedYear => selectedYear - 1);
                            }}
                        />

                        <Text color="primary" className="text-2xl font-bold">
                            {selectedYear}
                        </Text>

                        <Button
                            icon="pi pi-arrow-right"
                            outlined
                            rounded
                            className="border-none"
                            disabled={selectedYear >= new Date().getFullYear()}
                            onClick={() => {
                                setSelectedYear(old => {
                                    if (old >= new Date().getFullYear()) {
                                        return old;
                                    }
                                    return selectedYear + 1;
                                });
                            }}
                        />
                    </Container>

                    <Container flexColumn fullWidth>
                        <Chart 
                            type="bar" 
                            data={{
                                labels: evolutionYear
                                    .map((item) => {
                                        const monthReference = moment(item.month_reference, "YYYYMM").toDate();

                                        return months[monthReference.getMonth()];
                                    }) ?? [],
                                datasets: [
                                    {
                                        label: "Evolução mensal",
                                        data: (evolutionYear
                                            .map((item) => item.month_evolution_value) ?? []),
                                        backgroundColor: evolutionYear.map(
                                            evolution => {
                                                if (evolution.month_evolution_value > 0) {
                                                    return documentStyle.getPropertyValue('--primary-color')
                                                }

                                                return documentStyle.getPropertyValue('--attention-color');
                                            }
                                        )
                                    }
                                ]
                            }}
                            options={{
                                maintainAspectRatio: false,
                                aspectRatio: 0.8,
                                plugins: {
                                    legend: {
                                        display: false
                                    },
                                    tooltip: {
                                        callbacks: {
                                            label: function (context: any) {
                                                const value = context.raw || 0;
                                                const formattedValue = new Intl.NumberFormat("pt-BR", {
                                                    style: "currency",
                                                    currency: "BRL",
                                                }).format(value);
                        
                                                return ` ${formattedValue}`;
                                            },
                                        },
                                    }
                                },
                                scales: {
                                    x: {
                                        ticks: {
                                            color: textColorSecondary,
                                            font: {
                                                weight: 500
                                            }
                                        },
                                        grid: {
                                            display: false,
                                            drawBorder: false
                                        }
                                    },
                                    y: {
                                        stacked: true,
                                        ticks: {
                                            color: textColorSecondary,
                                            callback: function (value: any) {
                                                return new Intl.NumberFormat("pt-BR", {
                                                    style: "currency",
                                                    currency: "BRL",
                                                }).format(value);
                                            }
                                        },
                                        grid: {
                                            color: surfaceBorder
                                        }
                                    }
                                }
                            }} 
                        />
                    </Container>
                </Card>

                <Card
                    title="Movimentação patrimonial do período"
                    subTitle="Movimentação total do patrimônio, não reflete a carteira atual"
                    className="w-full"
                >
                    <Container flexColumn fullWidth>
                        <Chart 
                            type="bar" 
                            data={{
                                labels: [
                                    "Saldo inicial",
                                    "Aportes",
                                    "Proventos",
                                    "Rendimentos",
                                    "Resgates",
                                    "Ganho de capital",
                                    "Saldo final"
                                ],
                                datasets: [
                                    {
                                        label: "Ganho de capital",
                                        data: [
                                            [
                                                0, 
                                                (evolutionQuery.data?.equity_analysis.initial_balance ?? 0)
                                            ],
                                            [
                                                (evolutionQuery.data?.equity_analysis.initial_balance ?? 0), 
                                                (evolutionQuery.data?.equity_analysis.initial_balance ?? 0) + (evolutionQuery.data?.equity_analysis.total_charges ?? 0)
                                            ],
                                            [
                                                (evolutionQuery.data?.equity_analysis.initial_balance ?? 0) + (evolutionQuery.data?.equity_analysis.total_charges ?? 0), 
                                                (evolutionQuery.data?.equity_analysis.initial_balance ?? 0) + (evolutionQuery.data?.equity_analysis.total_charges ?? 0) + (evolutionQuery.data?.equity_analysis.total_earnings ?? 0)
                                            ],
                                            [
                                                (evolutionQuery.data?.equity_analysis.initial_balance ?? 0) + (evolutionQuery.data?.equity_analysis.total_charges ?? 0) + (evolutionQuery.data?.equity_analysis.total_earnings ?? 0),
                                                (evolutionQuery.data?.equity_analysis.initial_balance ?? 0) + (evolutionQuery.data?.equity_analysis.total_charges ?? 0) + (evolutionQuery.data?.equity_analysis.total_earnings ?? 0) + (evolutionQuery.data?.equity_analysis.total_income ?? 0), 
                                            ],
                                            [
                                                (evolutionQuery.data?.equity_analysis.initial_balance ?? 0) + (evolutionQuery.data?.equity_analysis.total_charges ?? 0) + (evolutionQuery.data?.equity_analysis.total_earnings ?? 0) + (evolutionQuery.data?.equity_analysis.total_income ?? 0), 
                                                ((evolutionQuery.data?.equity_analysis.initial_balance ?? 0) + (evolutionQuery.data?.equity_analysis.total_charges ?? 0) + (evolutionQuery.data?.equity_analysis.total_earnings ?? 0) + (evolutionQuery.data?.equity_analysis.total_income ?? 0)) + (evolutionQuery.data?.equity_analysis.total_withdrawals ?? 0)
                                            ],
                                            [
                                                ((evolutionQuery.data?.equity_analysis.initial_balance ?? 0) + (evolutionQuery.data?.equity_analysis.total_charges ?? 0) + (evolutionQuery.data?.equity_analysis.total_earnings ?? 0) + (evolutionQuery.data?.equity_analysis.total_income ?? 0)) + (evolutionQuery.data?.equity_analysis.total_withdrawals ?? 0),
                                                (((evolutionQuery.data?.equity_analysis.initial_balance ?? 0) + (evolutionQuery.data?.equity_analysis.total_charges ?? 0) + (evolutionQuery.data?.equity_analysis.total_earnings ?? 0) + (evolutionQuery.data?.equity_analysis.total_income ?? 0)) + (evolutionQuery.data?.equity_analysis.total_withdrawals ?? 0)) + (evolutionQuery.data?.equity_analysis.capital_gain ?? 0)
                                            ],
                                            [
                                                0,  
                                                (((evolutionQuery.data?.equity_analysis.initial_balance ?? 0) + (evolutionQuery.data?.equity_analysis.total_charges ?? 0) + (evolutionQuery.data?.equity_analysis.total_earnings ?? 0) + (evolutionQuery.data?.equity_analysis.total_income ?? 0)) + (evolutionQuery.data?.equity_analysis.total_withdrawals ?? 0)) + (evolutionQuery.data?.equity_analysis.capital_gain ?? 0)
                                            ]
                                        ],
                                        backgroundColor: [
                                            documentStyle.getPropertyValue('--primary-color'),
                                            documentStyle.getPropertyValue('--secondary-color'),
                                            documentStyle.getPropertyValue('--green-500'),
                                            (evolutionQuery.data?.equity_analysis.total_income ?? 0) > 0 ? documentStyle.getPropertyValue('--green-500') : documentStyle.getPropertyValue('--attention-color'),
                                            documentStyle.getPropertyValue('--attention-color'),
                                            documentStyle.getPropertyValue('--green-500'),
                                            documentStyle.getPropertyValue('--primary-color')
                                        ]
                                    }
                                ]
                            }}
                            options={{
                                maintainAspectRatio: false,
                                aspectRatio: 0.8,
                                plugins: {
                                    legend: {
                                        display: false
                                    },
                                    tooltip: {
                                        callbacks: {
                                            label: function (context: any) {
                                                const value: number[] = context.raw;

                                                const formattedValue = new Intl.NumberFormat("pt-BR", {
                                                    style: "currency",
                                                    currency: "BRL",
                                                }).format(value[1] - value[0]);

                                                if (context.label === "Saldo final") {
                                                    return ` ${formattedValue}` + "\n O saldo final é apresentado sem considerar o valor do resgate e o ganho de capital.";
                                                }

                                                return ` ${formattedValue}`;
                                            },
                                        },
                                    }
                                },
                                scales: {
                                    x: {
                                        ticks: {
                                            color: textColorSecondary,
                                            font: {
                                                weight: 500
                                            }
                                        },
                                        grid: {
                                            display: false,
                                            drawBorder: false
                                        }
                                    },
                                    y: {
                                        stacked: true,
                                        ticks: {
                                            color: textColorSecondary,
                                            callback: function (value: any) {
                                                return new Intl.NumberFormat("pt-BR", {
                                                    style: "currency",
                                                    currency: "BRL",
                                                }).format(value);
                                            }
                                        },
                                        grid: {
                                            color: surfaceBorder
                                        } 
                                    }
                                }
                            }} 
                        />
                    </Container>
                </Card>

                <Card
                    title="Evolução Mensal"
                    subTitle="Variação do patrimônio mensalmente"
                >
                    <Container className="table-responsive w-full">
                        <DataTable
                            value={
                                (evolutionQuery.data?.equity_analysis.equity_evolution_list ?? []).sort((a, b) => {
                                    return moment(a.month_reference, "YYYYMM").isAfter(moment(b.month_reference, "YYYYMM")) ? -1 : 1;
                                })
                            }
                            className="w-full"
                            emptyMessage="Nenhum registro encontrado"
                            paginator
                            rows={20}
                        >
                            <Column 
                                field="month_reference" 
                                header="Mês" 
                                sortable
                                body={(rowData: IMonthEvolution) => {
                                    const monthReference = moment(rowData.month_reference, "YYYYMM").toDate();

                                    return `${months[monthReference.getMonth()]} ${monthReference.getFullYear()}`;
                                }}
                            />
                            <Column 
                                field="month_initial_balance" 
                                header="Saldo"
                                sortable 
                                body={(rowData: IMonthEvolution) => (
                                    <Currency value={rowData.month_initial_balance}>
                                        {(value) => (
                                            <span>{value}</span>
                                        )}
                                    </Currency>
                                )}
                            />
                            <Column 
                                field="month_new_asset_value" 
                                header={
                                    <Text className="text-sm text-900 gap-2 flex">
                                        Aplicações
                                        <Icon name="pi pi-info-circle" />
                                    </Text>
                                }
                                headerTooltip="Todas as compras e novos investimentos realizadas no período."
                                headerTooltipOptions={{ position: "left" }}
                                sortable
                                body={(rowData: IMonthEvolution) => (
                                    <Currency value={rowData.month_new_asset_value}>
                                        {(value) => (
                                            <span>{value}</span>
                                        )}
                                    </Currency>
                                )}
                                style={{ minWidth: "200px" }}
                            />
                            <Column 
                                field="month_total_charges" 
                                header={
                                    <Text className="text-sm text-900 gap-2 flex">
                                        Aportes
                                        <Icon name="pi pi-info-circle" />
                                    </Text>
                                }
                                headerTooltip="Novas aplicações em investimentos já existentes."
                                headerTooltipOptions={{ position: "left" }}
                                sortable
                                body={(rowData: IMonthEvolution) => (
                                    <Currency value={rowData.month_total_charges}>
                                        {(value) => (
                                            <span>{value}</span>
                                        )}
                                    </Currency>
                                )}
                                style={{ minWidth: "200px" }}
                            />
                            <Column 
                                field="month_total_withdrawals" 
                                header={
                                    <Text className="text-sm text-900 gap-2 flex">
                                        Resgates
                                        <Icon name="pi pi-info-circle" />
                                    </Text>
                                }
                                headerTooltip="Saques, vendas e liquidações automáticas de ativos."
                                headerTooltipOptions={{ position: "left" }}
                                sortable
                                body={(rowData: IMonthEvolution) => (
                                    <Currency value={rowData.month_total_withdrawals}>
                                        {(value) => (
                                            <span>{value}</span>
                                        )}
                                    </Currency>
                                )}
                                style={{ minWidth: "200px" }}
                            />
                            <Column 
                                field="month_total_earnings" 
                                header={
                                    <Text className="text-sm text-900 gap-2 flex">
                                        Proventos
                                        <Icon name="pi pi-info-circle" />
                                    </Text>
                                }
                                headerTooltip="Dividendos, juros, amortizações, bônus e outros eventos corporativos automáticos."
                                headerTooltipOptions={{ position: "left" }}
                                sortable
                                body={(rowData: IMonthEvolution) => (
                                    <Currency value={rowData.month_total_earnings}>
                                        {(value) => (
                                            <span>{value}</span>
                                        )}
                                    </Currency>
                                )}
                                style={{ minWidth: "200px" }}
                            />
                            <Column 
                                field="month_total_income" 
                                header={
                                    <Text className="text-sm text-900 gap-2 flex">
                                        Rendimentos
                                        <Icon name="pi pi-info-circle" />
                                    </Text>
                                }
                                headerTooltip="Valorização ou desvalorização dos ativos da carteira no período."
                                headerTooltipOptions={{ position: "left" }}
                                sortable
                                body={(rowData: IMonthEvolution) => (
                                    <Currency value={rowData.month_total_income}>
                                        {(value) => (
                                            <span>{value}</span>
                                        )}
                                    </Currency>
                                )}
                                style={{ minWidth: "200px" }}
                            />
                            <Column 
                                field="month_capital_gain" 
                                header={
                                    <Text className="text-sm text-900 gap-2 flex">
                                        Ganho de capital
                                        <Icon name="pi pi-info-circle" />
                                    </Text>
                                }
                                headerTooltip="Montante referente às liquidações de vendas e resgates de ativos."
                                headerTooltipOptions={{ position: "left" }}
                                sortable
                                body={(rowData: IMonthEvolution) => (
                                    <Currency value={rowData.month_capital_gain}>
                                        {(value) => (
                                            <span>{value}</span>
                                        )}
                                    </Currency>
                                )}
                                style={{ minWidth: "220px" }}
                            />
                            <Column 
                                field="month_total_equity" 
                                header={
                                    <Text className="text-sm text-900 gap-2 flex">
                                        Saldo final
                                        <Icon name="pi pi-info-circle" />
                                    </Text>
                                }
                                headerTooltip="O saldo final considera a movimentação completa do período, inclusive ganho de capital e resgates."
                                headerTooltipOptions={{ position: "left" }}
                                sortable
                                body={(rowData: IMonthEvolution) => (
                                    <Currency value={rowData.month_total_equity}>
                                        {(value) => (
                                            <span>{value}</span>
                                        )}
                                    </Currency>
                                )}
                                style={{ minWidth: "200px" }}
                            />
                        </DataTable>
                    </Container>
                </Card>
            </Container>
        </AnalysisContainer>
    );
}

export default EvolutionPage;
